import { roleAtLeast, type PromptVersionSummary, type StaleAgentView } from '@ai-sales/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  createPromptVersion,
  fetchPrompt,
  fetchPromptDiff,
  fetchPromptVersion,
  publishPromptVersion,
} from '../api/agents.api.js'
import { Badge } from '../components/badge.js'
import { Button } from '../components/button.js'
import { ConfirmDialog } from '../components/confirm-dialog.js'
import { TextDiffView } from '../components/diff-view.js'
import { ErrorPanel } from '../components/error-panel.js'
import { Field } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import { formatDateTime, formatRelative } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

export function PromptPage() {
  const workspace = useCurrentWorkspace()
  const { promptId = '' } = useParams()
  const queryClient = useQueryClient()

  const canEdit = roleAtLeast(workspace.role, 'admin')

  const prompt = useQuery({
    queryKey: ['prompt', workspace.id, promptId],
    queryFn: () => fetchPrompt(workspace.id, promptId),
  })

  const [draft, setDraft] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [compareFrom, setCompareFrom] = useState<string | null>(null)
  const [compareTo, setCompareTo] = useState<string | null>(null)
  const [toPublish, setToPublish] = useState<PromptVersionSummary | null>(null)
  const [staleNotice, setStaleNotice] = useState<string[] | null>(null)
  const [sameNotice, setSameNotice] = useState(false)

  const publishedId = prompt.data?.publishedVersion?.id ?? null

  /**
   * Черновик заполняется из опубликованной версии и перезаполняется
   * только тогда, когда опубликованная версия сменилась.
   *
   * Зависимость именно от идентификатора версии, а не от `prompt.data`
   * и не от «черновик пуст». Первое стирало бы набранный текст при любом
   * фоновом обновлении — незаметно и ровно тогда, когда человек отвлёкся.
   * Второе выглядит безопасным и ломается иначе: после публикации
   * черновик обнуляли, эффект срабатывал немедленно и подставлял **ещё
   * не обновившийся** текст, поэтому в редакторе оставалась прежняя
   * версия. Нашлось это только прогоном интерфейса в браузере: и типы,
   * и запросы к серверу были в порядке.
   */
  useEffect(() => {
    if (prompt.data) setDraft(prompt.data.currentBody)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedId])

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['prompt', workspace.id, promptId] }),
      queryClient.invalidateQueries({ queryKey: ['prompts', workspace.id] }),
    ])

  const saveVersion = useMutation({
    mutationFn: (input: { body: string; note: string; publish: boolean }) =>
      createPromptVersion(workspace.id, promptId, input),
    onSuccess: (result) => {
      setNote('')
      setSameNotice(!result.created)
      setStaleNotice(describeStale(result.staleAgents))
      void invalidate()
    },
  })

  const publish = useMutation({
    mutationFn: (versionId: string) => publishPromptVersion(workspace.id, promptId, versionId),
    onSuccess: (result) => {
      setToPublish(null)
      setStaleNotice(describeStale(result.staleAgents))
      void invalidate()
    },
  })

  const diff = useQuery({
    queryKey: ['prompt-diff', workspace.id, promptId, compareFrom, compareTo],
    queryFn: () => fetchPromptDiff(workspace.id, promptId, compareFrom ?? '', compareTo ?? ''),
    enabled: compareFrom !== null && compareTo !== null,
  })

  const [previewId, setPreviewId] = useState<string | null>(null)

  const preview = useQuery({
    queryKey: ['prompt-version', workspace.id, promptId, previewId],
    queryFn: () => fetchPromptVersion(workspace.id, promptId, previewId ?? ''),
    enabled: previewId !== null,
  })

  if (prompt.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted">
        <Spinner /> Загружаем промпт…
      </div>
    )
  }

  if (prompt.error || !prompt.data) {
    return <ErrorPanel error={prompt.error} onRetry={() => void prompt.refetch()} />
  }

  const data = prompt.data
  const published = data.publishedVersion

  return (
    <>
      <PageHeader
        title={data.name}
        description={
          <>
            {data.description || 'Без пояснения'} ·{' '}
            <Link to={`/w/${workspace.id}/prompts`} className="text-accent hover:underline">
              ко всем промптам
            </Link>
          </>
        }
        actions={
          published ? (
            <Badge tone="ok">опубликована версия {published.version}</Badge>
          ) : (
            <Badge tone="warn">ни одна версия не опубликована</Badge>
          )
        }
      />

      {staleNotice && (
        <div className="mb-5 rounded-md border border-copper/40 bg-copper-soft p-4">
          <p className="text-sm font-medium text-copper">
            Агенты остались на прежней версии этого промпта
          </p>
          <p className="mt-1 text-xs text-ink-soft">
            Так и задумано: версия агента закрепляет версию промпта, иначе правка текста меняла бы
            поведение задним числом. Чтобы агент заговорил по-новому, создайте его новую версию.
          </p>
          <ul className="mt-2 list-inside list-disc text-xs text-ink-soft">
            {staleNotice.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex flex-col gap-5">
          <Panel
            title="Текст"
            description={
              canEdit
                ? 'Сохранение создаёт следующую версию. Прежние остаются нетронутыми.'
                : 'Править промпты может администратор воркспейса.'
            }
          >
            <textarea
              value={draft ?? ''}
              onChange={(event) => {
                setDraft(event.target.value)
                setSameNotice(false)
              }}
              readOnly={!canEdit}
              rows={18}
              className="w-full rounded-md border border-line bg-raised px-3 py-2 font-mono text-sm text-ink"
            />

            {canEdit && (
              <div className="mt-4 flex flex-col gap-3">
                <Field
                  label="Что изменилось"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={300}
                  placeholder="Убрал обещание сроков доставки"
                  hint="Попадёт в историю версий — через месяц это единственное, что объяснит правку"
                />

                {sameNotice && (
                  <p className="rounded-md border border-line bg-raised px-3 py-2 text-xs text-muted">
                    Текст не изменился, новая версия не создана.
                  </p>
                )}

                {saveVersion.error != null && <ErrorPanel error={saveVersion.error} />}

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    loading={saveVersion.isPending}
                    onClick={() =>
                      saveVersion.mutate({ body: draft ?? '', note, publish: true })
                    }
                  >
                    Сохранить и опубликовать
                  </Button>
                  <Button
                    loading={saveVersion.isPending}
                    onClick={() =>
                      saveVersion.mutate({ body: draft ?? '', note, publish: false })
                    }
                  >
                    Сохранить как черновик
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDraft(data.currentBody)
                      setSameNotice(false)
                    }}
                  >
                    Вернуть опубликованный текст
                  </Button>
                </div>
              </div>
            )}
          </Panel>

          {compareFrom && compareTo && (
            <Panel
              title="Сравнение версий"
              actions={
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCompareFrom(null)
                    setCompareTo(null)
                  }}
                >
                  Закрыть
                </Button>
              }
            >
              {diff.isLoading && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Spinner /> Считаем разницу…
                </p>
              )}
              {diff.error != null && <ErrorPanel error={diff.error} />}
              {diff.data && <TextDiffView diff={diff.data} />}
            </Panel>
          )}

          {previewId && (
            <Panel
              title={`Текст версии ${preview.data?.version ?? ''}`}
              actions={
                <Button size="sm" variant="ghost" onClick={() => setPreviewId(null)}>
                  Закрыть
                </Button>
              }
            >
              {preview.isLoading && (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Spinner /> Загружаем…
                </p>
              )}
              {preview.error != null && <ErrorPanel error={preview.error} />}
              {preview.data && (
                <pre className="overflow-x-auto rounded-md border border-line bg-raised p-3 font-mono text-xs whitespace-pre-wrap text-ink">
                  {preview.data.body}
                </pre>
              )}
            </Panel>
          )}
        </div>

        <Panel
          title="История версий"
          description="Публикация и откат — одно и то же действие: переставить указатель"
        >
          <ol className="flex flex-col gap-2">
            {data.versions.map((version) => (
              <li
                key={version.id}
                className="rounded-md border border-line bg-raised px-3 py-2.5 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-ink">Версия {version.version}</span>
                  {version.isPublished && <Badge tone="ok">опубликована</Badge>}
                </div>

                <p className="mt-0.5 text-xs text-muted">
                  {formatRelative(version.createdAt)} · {version.createdByEmail ?? 'автор удалён'} ·{' '}
                  {version.bodyLength} знаков
                </p>

                {version.note && <p className="mt-1 text-xs text-ink-soft">{version.note}</p>}

                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => setPreviewId(version.id)}>
                    Показать
                  </Button>

                  {published && published.id !== version.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        /**
                         * Направление: от опубликованной к выбранной.
                         * Так строка читается как «что изменится, если
                         * опубликовать эту версию». Обратный порядок
                         * показывает то же самое зеркально, и человек
                         * читает добавленное как удалённое.
                         */
                        setCompareFrom(published.id)
                        setCompareTo(version.id)
                      }}
                    >
                      Сравнить с опубликованной
                    </Button>
                  )}

                  {canEdit && !version.isPublished && (
                    <Button size="sm" onClick={() => setToPublish(version)}>
                      {published && version.version < published.version
                        ? 'Откатить сюда'
                        : 'Опубликовать'}
                    </Button>
                  )}
                </div>

                <p className="mt-1 text-[11px] text-muted" title={formatDateTime(version.createdAt)}>
                  {formatDateTime(version.createdAt)}
                </p>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      <ConfirmDialog
        open={toPublish !== null}
        onOpenChange={(open) => !open && setToPublish(null)}
        title={`Опубликовать версию ${toPublish?.version ?? ''}?`}
        description={
          published && toPublish && toPublish.version < published.version
            ? `Это откат: сейчас опубликована версия ${published.version}. Прежние версии остаются на месте, новая не создаётся — переставляется указатель.`
            : 'Новые диалоги пойдут по этой версии. Уже идущие останутся на закреплённой за ними версии агента.'
        }
        confirmLabel="Опубликовать"
        loading={publish.isPending}
        onConfirm={() => toPublish && publish.mutate(toPublish.id)}
      />

      {publish.error != null && <ErrorPanel error={publish.error} className="mt-4" />}
    </>
  )
}

function describeStale(staleAgents: StaleAgentView[]): string[] | null {
  return staleAgents.length === 0
    ? null
    : staleAgents.map(
        (item) =>
          `${item.agentName} — версия ${item.agentVersion}, закреплена за версией ${item.pinnedPromptVersion} промпта`,
      )
}
